import {
	ArrowDropDown,
	ArrowRight,
	CalendarMonth,
	Check,
	CreditCard,
	Folder,
	FolderOpen,
	HourglassEmpty,
	Person,
	Receipt,
	Schedule,
	UnfoldMore,
	UnfoldLess,
} from "@mui/icons-material";
import { Box, Button, Chip, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import {
	DataGridPremium,
	useGridApiRef,
	type GridCellParams,
	type GridColDef,
	type GridRenderCellParams,
	type GridRowId,
	type GridRowParams,
} from "@mui/x-data-grid-premium";
import { useTranslation } from "next-i18next";
import { useCallback, useMemo, useEffect, useRef, useState } from "react";

import type { MonthlyProjectOverviewItem } from "../types/MonthlyProjectOverviewTypes";

/** Renders a centered dash placeholder for empty cells */
const EmptyCell = ({ align = "center" }: { align?: "center" | "right" }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: align === "right" ? "flex-end" : "center",
			width: "100%",
			height: "100%",
		}}>
		<Typography color="text.secondary">-</Typography>
	</Box>
);

export type PaginationModel = {
	page: number;
	pageSize: number;
};

export type MonthlyOverviewDataGridProps = {
	/** Root-level items from the server */
	rows: MonthlyProjectOverviewItem[];
	/** Lazy-loaded child items */
	subRows?: MonthlyProjectOverviewItem[];
	rowCount: number;
	isLoading: boolean;

	paginationModel: PaginationModel;
	onPaginationModelChange: (model: PaginationModel) => void;

	onCreateMonthlyBilling?: (item: MonthlyProjectOverviewItem) => void;

	/** Called when a document chip is clicked to preview the document */
	onPreviewDocument?: (documentId: string) => void;

	/** Called when a row is expanded to load its children (lazy loading) */
	onExpandRow?: (
		id: string,
		path: string[],
		itemType: MonthlyProjectOverviewItem["itemType"],
	) => Promise<void>;

	/** Called to expand all rows - loads all children */
	onExpandAll?: () => Promise<void>;

	/** When true, hides the customer column (since client is already shown as root level) */
	groupByClient: boolean;

	/** IDs of projects that directly match the search criteria - used for highlighting */
	matchedProjectIds?: string[];

	/** When true, indicates we're in search mode (full tree is returned) */
	isSearchMode?: boolean;
};

const formatMinutesToHours = (minutes: number): string => {
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	if (mins === 0) {
		return `${hours}h`;
	}
	return `${hours}h ${mins}m`;
};

const getStatusColor = (
	status: string,
): "default" | "primary" | "success" | "warning" | "error" => {
	switch (status?.toUpperCase()) {
		case "APPROVED":
			return "success";
		case "PENDING":
			return "warning";
		case "DRAFT":
			return "default";
		case "FINALIZED":
			return "primary";
		case "CANCELLED":
			return "error";
		default:
			return "default";
	}
};

export const MonthlyOverviewDataGrid = ({
	rows,
	subRows = [],
	rowCount,
	isLoading,
	paginationModel,
	onPaginationModelChange,
	onCreateMonthlyBilling,
	onPreviewDocument,
	onExpandRow,
	onExpandAll,
	groupByClient,
	matchedProjectIds = [],
	isSearchMode = false,
}: MonthlyOverviewDataGridProps) => {
	const { t } = useTranslation();
	const apiRef = useGridApiRef();

	// Track rows that are pending expansion (waiting for children to load)
	const pendingExpansionRef = useRef<Set<string>>(new Set());

	// Track if we're currently expanding all
	const [isExpandingAll, setIsExpandingAll] = useState(false);

	// Combine rows and subRows for rendering (must be before callbacks that use it)
	const allRows = useMemo(() => [...rows, ...subRows], [rows, subRows]);

	// Expand all handler
	const handleExpandAll = useCallback(async () => {
		if (!onExpandAll || isSearchMode) return;

		setIsExpandingAll(true);
		console.log("[handleExpandAll] Starting expand all");

		try {
			await onExpandAll();

			// After loading, expand all rows in the DataGrid
			setTimeout(() => {
				if (apiRef.current) {
					allRows.forEach((row) => {
						if (
							row.isGroup ||
							row.itemType === "CLIENT" ||
							row.itemType === "PROJECT" ||
							row.itemType === "EMPLOYEE"
						) {
							const rowNode = apiRef.current.getRowNode(row.id);
							if (rowNode && "childrenExpanded" in rowNode) {
								apiRef.current.setRowChildrenExpansion(row.id, true);
							}
						}
					});
				}
				console.log("[handleExpandAll] All rows expanded");
			}, 100);
		} finally {
			setIsExpandingAll(false);
		}
	}, [onExpandAll, isSearchMode, apiRef, allRows]);

	// Collapse all handler
	const handleCollapseAll = useCallback(() => {
		if (!apiRef.current) return;

		console.log("[handleCollapseAll] Collapsing all rows");
		allRows.forEach((row) => {
			const rowNode = apiRef.current.getRowNode(row.id);
			if (rowNode && "childrenExpanded" in rowNode && rowNode.childrenExpanded) {
				apiRef.current.setRowChildrenExpansion(row.id, false);
			}
		});
	}, [apiRef, allRows]);

	// Auto-size the actions column to fit its content
	useEffect(() => {
		if (!apiRef.current || isLoading || allRows.length === 0) return;
		const timeoutId = setTimeout(() => {
			apiRef.current.autosizeColumns({
				columns: ["actions"],
				includeHeaders: true,
			});
		}, 100);
		return () => clearTimeout(timeoutId);
	}, [apiRef, allRows, isLoading]);

	// Effect to expand rows when their children become available
	useEffect(() => {
		const api = apiRef.current;
		console.log(
			"[useEffect expand] subRows changed, count:",
			subRows.length,
			"pending:",
			pendingExpansionRef.current.size,
		);

		if (!api || pendingExpansionRef.current.size === 0) return;

		// Use setTimeout to ensure DataGrid has processed the new rows
		const timeoutId = setTimeout(() => {
			// Check each pending row to see if children are now available
			const toRemove: string[] = [];
			pendingExpansionRef.current.forEach((parentId) => {
				const hasChildren = subRows.some((row) => row.parentId === parentId);
				console.log("[useEffect expand] Checking parentId:", parentId, "hasChildren:", hasChildren);
				if (hasChildren) {
					console.log("[useEffect expand] Expanding row:", parentId);
					api.setRowChildrenExpansion(parentId, true);
					toRemove.push(parentId);
				}
			});

			// Remove expanded rows from pending set
			toRemove.forEach((id) => pendingExpansionRef.current.delete(id));
			console.log(
				"[useEffect expand] After processing, pending:",
				pendingExpansionRef.current.size,
			);
		}, 50);

		return () => clearTimeout(timeoutId);
	}, [subRows, apiRef]);

	// Create a Set for efficient lookup of matched project IDs
	const matchedProjectIdsSet = useMemo(() => new Set(matchedProjectIds), [matchedProjectIds]);

	// Expand parent rows when search matches change
	useEffect(() => {
		const api = apiRef.current;
		if (matchedProjectIds.length > 0 && api) {
			// Expand all group rows to show search results
			rows.forEach((row) => {
				if (row.itemType === "CLIENT" || row.itemType === "PROJECT") {
					const rowNode = api.getRowNode(row.id);
					// Only expand if it's actually a group node with children
					if (rowNode && "childrenExpanded" in rowNode) {
						api.setRowChildrenExpansion(row.id, true);
					}
				}
			});
		}
	}, [matchedProjectIds, rows, apiRef]);

	// Get row class name for highlighting
	const getRowClassName = useCallback(
		(params: GridRowParams<MonthlyProjectOverviewItem>) => {
			const row = params.row;
			// Highlight if this row's project ID is in the matched set
			if (row.projectId && matchedProjectIdsSet.has(row.projectId)) {
				return "Mui-highlighted-row";
			}
			return "";
		},
		[matchedProjectIdsSet],
	);

	const getDisplayName = useCallback(
		(item: MonthlyProjectOverviewItem): string => {
			switch (item.itemType) {
				case "CLIENT":
					if (item.isInternalProjectsGroup) return t("internal-projects");
					return item.client?.name ?? item.clientName ?? t("unknown-client");
				case "PROJECT":
					return item.project?.name ?? item.projectName ?? t("unknown-project");
				case "EMPLOYEE":
					return item.employee?.name ?? item.employeeName ?? t("unknown-employee");
				case "TIMESHEET":
					return item.timesheetNumber ?? t("timesheet");
				default:
					return "";
			}
		},
		[t],
	);

	const getItemIcon = useCallback((item: MonthlyProjectOverviewItem, isExpanded: boolean) => {
		switch (item.itemType) {
			case "CLIENT":
				return isExpanded ? (
					<FolderOpen fontSize="small" color="primary" sx={{ mr: 0.5 }} />
				) : (
					<Folder fontSize="small" color="primary" sx={{ mr: 0.5 }} />
				);
			case "PROJECT":
				// Tender (framework contract) gets a folder icon
				if (item.isGroup || item.projectType === "TENDER") {
					return isExpanded ? (
						<FolderOpen fontSize="small" color="secondary" sx={{ mr: 0.5 }} />
					) : (
						<Folder fontSize="small" color="secondary" sx={{ mr: 0.5 }} />
					);
				}
				return null;
			case "EMPLOYEE":
				return <Person fontSize="small" color="action" sx={{ mr: 0.5 }} />;
			default:
				return null;
		}
	}, []);

	// Handle tree expansion with lazy loading
	const handleExpandClick = useCallback(
		async (params: GridRenderCellParams<MonthlyProjectOverviewItem>) => {
			console.log(
				"[handleExpandClick] Called for row:",
				params.row?.id,
				"itemType:",
				params.row?.itemType,
				"path:",
				params.row?.path,
			);

			if (!apiRef.current) {
				console.log("[handleExpandClick] apiRef.current is null");
				return;
			}

			const row = params.row;
			const rowNode = apiRef.current.getRowNode(row.id);
			const isExpanded =
				rowNode && "childrenExpanded" in rowNode ? Boolean(rowNode.childrenExpanded) : false;

			console.log("[handleExpandClick] isExpanded:", isExpanded, "rowNode:", rowNode);

			if (isExpanded) {
				// Collapsing - just collapse
				console.log("[handleExpandClick] Collapsing row");
				apiRef.current.setRowChildrenExpansion(row.id, false);
			} else {
				// Expanding - check if children are already loaded
				const hasLoadedChildrenInSubRows = subRows.some((subRow) => subRow.parentId === row.id);

				console.log(
					"[handleExpandClick] hasLoadedChildrenInSubRows:",
					hasLoadedChildrenInSubRows,
					"onExpandRow:",
					!!onExpandRow,
					"isSearchMode:",
					isSearchMode,
				);

				if (hasLoadedChildrenInSubRows) {
					// Children already loaded, just expand
					console.log("[handleExpandClick] Children already in subRows, expanding directly");
					apiRef.current.setRowChildrenExpansion(row.id, true);
				} else if (onExpandRow && !isSearchMode) {
					// Need to load children first, add to pending and load
					console.log("[handleExpandClick] Loading children via onExpandRow with path:", row.path);
					pendingExpansionRef.current.add(row.id);
					await onExpandRow(row.id, row.path, row.itemType);
				} else {
					console.log(
						"[handleExpandClick] Not loading children - onExpandRow:",
						!!onExpandRow,
						"isSearchMode:",
						isSearchMode,
					);
				}
			}
		},
		[apiRef, onExpandRow, isSearchMode, subRows],
	);

	// Column definitions
	const columns: GridColDef<MonthlyProjectOverviewItem>[] = useMemo(
		() => [
			// Customer column - only visible when NOT grouped by client
			...(!groupByClient
				? [
						{
							field: "customer",
							headerName: t("customer"),
							width: 200,
							renderCell(params: GridCellParams<MonthlyProjectOverviewItem>) {
								const row = params.row;
								// Only show customer for regular PROJECT items (not Tenders or Internal)
								if (
									row.itemType !== "PROJECT" ||
									row.projectType === "TENDER" ||
									row.projectType === "INTERNAL"
								) {
									return null;
								}
								const customerName = row.client?.name ?? t("unknown-client");
								return (
									<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
										<Typography variant="body2" noWrap>
											{customerName}
										</Typography>
									</Box>
								);
							},
						} as GridColDef<MonthlyProjectOverviewItem>,
					]
				: []),
			// Project Number column
			{
				field: "projectNumber",
				headerName: t("project-number"),
				width: 130,
				renderCell(params: GridCellParams<MonthlyProjectOverviewItem>) {
					const row = params.row;
					// Only show project number for regular PROJECT items (not Tenders)
					if (row.itemType !== "PROJECT" || row.projectType === "TENDER") {
						return null;
					}
					const projectNumber = row.project?.number ?? row.projectNumber;
					if (!projectNumber) {
						return null;
					}
					return (
						<Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
							<Typography variant="body2" noWrap>
								{projectNumber}
							</Typography>
						</Box>
					);
				},
			},
			// Budget utilization column
			{
				field: "budgetUtilization",
				headerName: t("budget"),
				width: 150,
				renderCell(params: GridCellParams<MonthlyProjectOverviewItem>) {
					const row = params.row;
					// Only show budget for PROJECT items (not Tenders, Clients, Employees, Timesheets)
					if (row.itemType !== "PROJECT" || row.projectType === "TENDER") {
						return null;
					}
					const budget = row.budgetStatus;
					if (!budget || budget.totalBudgetMinutes === 0) {
						return <EmptyCell />;
					}

					const utilizationPercent = Math.min(budget.minutesUtilizationPercent, 100);
					const isOverBudget = budget.minutesUtilizationPercent > 100;

					return (
						<Tooltip
							title={`${formatMinutesToHours(budget.spentMinutes)} / ${formatMinutesToHours(budget.totalBudgetMinutes)}`}>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									width: "100%",
									height: "100%",
									gap: 0.25,
								}}>
								<Typography variant="caption" sx={{ lineHeight: 1 }}>
									{budget.minutesUtilizationPercent.toFixed(0)}%
								</Typography>
								<LinearProgress
									variant="determinate"
									value={utilizationPercent}
									color={isOverBudget ? "error" : utilizationPercent > 80 ? "warning" : "primary"}
									sx={{ width: "100%", height: 6, borderRadius: 1 }}
								/>
							</Box>
						</Tooltip>
					);
				},
			},
			{
				field: "hours",
				headerName: t("hours"),
				width: 160,
				renderCell(params: GridCellParams<MonthlyProjectOverviewItem>) {
					const row = params.row;
					if (row.itemType === "CLIENT" || row.projectType === "TENDER") {
						return null;
					}
					const open = row.openMinutes ?? 0;
					const pending = row.pendingMinutes ?? 0;
					const approved = row.approvedMinutes ?? 0;
					if (open === 0 && pending === 0 && approved === 0) return <EmptyCell />;

					return (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								height: "100%",
								gap: 0.25,
								py: 0.5,
							}}>
							{open > 0 && (
								<Tooltip title={t("open-hours-tooltip")}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
										<HourglassEmpty sx={{ fontSize: 14 }} />
										<Typography variant="caption">{formatMinutesToHours(open)}</Typography>
									</Box>
								</Tooltip>
							)}
							{pending > 0 && (
								<Tooltip title={t("pending-hours-tooltip")}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "warning.main" }}>
										<Schedule sx={{ fontSize: 14 }} />
										<Typography variant="caption">{formatMinutesToHours(pending)}</Typography>
									</Box>
								</Tooltip>
							)}
							{approved > 0 && (
								<Tooltip title={t("approved-hours-tooltip")}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "success.main" }}>
										<Check sx={{ fontSize: 14 }} />
										<Typography variant="caption">{formatMinutesToHours(approved)}</Typography>
									</Box>
								</Tooltip>
							)}
						</Box>
					);
				},
			},
			{
				field: "documents",
				headerName: t("documents"),
				width: 200,
				renderCell(params: GridCellParams<MonthlyProjectOverviewItem>) {
					const row = params.row;
					if (row.itemType === "CLIENT" || row.projectType === "TENDER") {
						return null;
					}
					const invoices = row.clientInvoices ?? [];
					const creditNotes = row.supplierCreditNotes ?? [];
					if (invoices.length === 0 && creditNotes.length === 0) return <EmptyCell />;

					return (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								height: "100%",
								gap: 0.25,
								py: 0.5,
							}}>
							{invoices.map((inv) => (
								<Chip
									key={inv.invoiceId}
									icon={<Receipt sx={{ fontSize: 12 }} />}
									label={`${t("invoice")}: ${t(`document-status.${inv.status.toLowerCase()}`)}`}
									color={getStatusColor(inv.status)}
									size="small"
									variant="outlined"
									onClick={
										onPreviewDocument ? () => onPreviewDocument(inv.invoiceId) : undefined
									}
									clickable={!!onPreviewDocument}
									sx={{ height: 20, "& .MuiChip-label": { fontSize: "0.7rem", px: 0.5 } }}
								/>
							))}
							{creditNotes.map((cn) => (
								<Chip
									key={cn.creditNoteId}
									icon={<CreditCard sx={{ fontSize: 12 }} />}
									label={`${t("credit-note")}: ${t(`document-status.${cn.status.toLowerCase()}`)}`}
									color={getStatusColor(cn.status)}
									size="small"
									variant="outlined"
									onClick={
										onPreviewDocument ? () => onPreviewDocument(cn.creditNoteId) : undefined
									}
									clickable={!!onPreviewDocument}
									sx={{ height: 20, "& .MuiChip-label": { fontSize: "0.7rem", px: 0.5 } }}
								/>
							))}
						</Box>
					);
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				minWidth: 50,
				sortable: false,
				renderCell(params: GridCellParams<MonthlyProjectOverviewItem>) {
					const item = params.row;

					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
							{item.canCreateMonthlyBilling &&
								item.itemType === "PROJECT" &&
								onCreateMonthlyBilling && (
									<Tooltip title={t("create-monthly-billing")}>
										<Button
											size="small"
											variant="contained"
											color="primary"
											onClick={() => onCreateMonthlyBilling(item)}
											startIcon={<CalendarMonth />}
											data-testid="create-monthly-billing-button">
											{t("monthly-billing")}
										</Button>
									</Tooltip>
								)}
						</Box>
					);
				},
			},
		],
		[t, onCreateMonthlyBilling, onPreviewDocument, groupByClient],
	);

	// Grouping column definition for tree data
	const groupingColDef = useMemo(
		() => ({
			headerName: t("name"),
			width: 400,
			flex: 1,
			renderCell: (params: GridRenderCellParams<MonthlyProjectOverviewItem>) => {
				const row = params.row;
				if (!row) return null;

				const depth = params.rowNode?.depth ?? 0;
				const rowNode = apiRef.current?.getRowNode(params.id);
				const isExpanded =
					rowNode && "childrenExpanded" in rowNode ? Boolean(rowNode.childrenExpanded) : false;
				// Check if children are already loaded in the grid
				const hasLoadedChildren =
					rowNode &&
					"children" in rowNode &&
					Array.isArray(rowNode.children) &&
					rowNode.children.length > 0;
				// For lazy loading: show expand button if the item CAN have children (based on isGroup flag or item type)
				// CLIENT can have PROJECT children, PROJECT can have EMPLOYEE children, EMPLOYEE can have TIMESHEET children
				const canHaveChildren =
					row.isGroup ||
					row.itemType === "CLIENT" ||
					row.itemType === "PROJECT" ||
					row.itemType === "EMPLOYEE";
				const showExpandButton = hasLoadedChildren || (canHaveChildren && !isSearchMode);

				const displayName = getDisplayName(row);
				const icon = getItemIcon(row, isExpanded);

				// Determine font weight based on item type and isGroup flag
				const isGroupItem =
					row.isGroup || row.itemType === "CLIENT" || row.projectType === "TENDER";
				const fontWeight = isGroupItem ? 700 : row.itemType === "PROJECT" ? 600 : 400;

				// Determine background color for group items (Tenders/Clients)
				const bgColor = isGroupItem ? "action.hover" : "transparent";

				return (
					<Box
						sx={{
							ml: depth * 2.5,
							display: "flex",
							alignItems: "center",
							height: "100%",
							backgroundColor: bgColor,
							borderRadius: 1,
							px: isGroupItem ? 0.5 : 0,
						}}>
						{showExpandButton ? (
							<IconButton
								size="small"
								onClick={() => handleExpandClick(params)}
								data-testid="expand-row-button"
								sx={{ mr: 0.5 }}>
								{isExpanded ? <ArrowDropDown /> : <ArrowRight />}
							</IconButton>
						) : (
							<Box sx={{ width: 28 }} />
						)}
						{icon}
						<Typography variant="body2" noWrap sx={{ fontWeight }}>
							{displayName}
						</Typography>
						{row.itemType === "TIMESHEET" && row.timesheetStatus && (
							<Chip
								label={t(`timesheet-status.${row.timesheetStatus.toLowerCase()}`)}
								color={getStatusColor(row.timesheetStatus)}
								size="small"
								sx={{ ml: 1 }}
							/>
						)}
						{row.itemType === "PROJECT" && row.projectType === "TENDER" && (
							<Chip
								label={t("framework-contract")}
								color="secondary"
								variant="outlined"
								size="small"
								sx={{ ml: 1 }}
							/>
						)}
						{row.itemType === "PROJECT" && row.projectType === "INTERNAL" && (
							<Chip
								label={t("internal-project")}
								color="info"
								variant="outlined"
								size="small"
								sx={{ ml: 1 }}
							/>
						)}
						{row.isInternalProjectsGroup && (
							<Chip
								label={t("internal-projects")}
								color="info"
								variant="outlined"
								size="small"
								sx={{ ml: 1 }}
							/>
						)}
					</Box>
				);
			},
		}),
		[t, apiRef, handleExpandClick, getDisplayName, getItemIcon, isSearchMode],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
			{/* Toolbar with expand/collapse buttons */}
			{!isSearchMode && onExpandAll && (
				<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
					<Button
						size="small"
						variant="outlined"
						startIcon={isExpandingAll ? <HourglassEmpty /> : <UnfoldMore />}
						onClick={handleExpandAll}
						disabled={isExpandingAll || isLoading}
						data-testid="expand-all-button">
						{isExpandingAll ? t("loading") : t("expand-all")}
					</Button>
					<Button
						size="small"
						variant="outlined"
						startIcon={<UnfoldLess />}
						onClick={handleCollapseAll}
						disabled={isLoading}
						data-testid="collapse-all-button">
						{t("collapse-all")}
					</Button>
				</Box>
			)}

			<DataGridPremium
				apiRef={apiRef}
				rows={allRows}
				columns={columns}
				rowCount={rowCount}
				localeText={{
					paginationRowsPerPage: t("pagination.rows-per-page", { ns: "common" }),
				}}
				// Pagination
				pagination
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={onPaginationModelChange}
				pageSizeOptions={[10, 20, 25, 50, 100]}
				initialState={{ pinnedColumns: { right: ["actions"] } }}
				// Tree Data
				treeData
				getTreeDataPath={(row: MonthlyProjectOverviewItem) => row.path || [row.id]}
				groupingColDef={groupingColDef}
				defaultGroupingExpansionDepth={1}
				// Styling
				getRowId={(row: GridRowParams | MonthlyProjectOverviewItem) => row.id as GridRowId}
				getRowClassName={getRowClassName}
				// Loading
				loading={isLoading || isExpandingAll}
				// Other
				disableRowSelectionOnClick
				getRowHeight={() => "auto"}
				className="table"
				sx={{
					flex: 1,
					border: "none",
					backgroundColor: "transparent",
					// Highlighting for matched search results
					"& .Mui-highlighted-row": {
						backgroundColor: "rgba(25, 118, 210, 0.12)",
						"&:hover": {
							backgroundColor: "rgba(25, 118, 210, 0.18)",
						},
					},
				}}
				data-testid="monthly-overview-data-grid"
			/>
		</Box>
	);
};
